import { Box, Button, Typography, useTheme, Paper } from "@mui/material";
import FlexBetween from "components/FlexBetween";
import Header from "components/Header";
import StudentReport from "components/StudentReport";
import { Age } from "components/StudentsGrid";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Cell, Pie, PieChart } from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useGetStudentDetailQuery } from "state/api";
import { Student } from "types/Student";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const StudentReportPage = () => {
    const ref1 = useRef(null);
    const ref2 = useRef(null);
    const ref3 = useRef(null);
    const { palette } = useTheme();
    const { id } = useParams();
    const { data: studentData, isLoading: isStudentDataLoading } =
        useGetStudentDetailQuery(Number(id));

    const capitalizeName = (name: string | undefined): string => {
        if (!name || name === "") return "";
        return name
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const calculateAge = (dobString?: string): Age => {
        if (!dobString) {
            return { years: 0, months: 0 };
        }
        const dob = new Date(dobString);
        const now = new Date();

        let years = now.getFullYear() - dob.getFullYear();
        let months = now.getMonth() - dob.getMonth();

        // Adjust years and months if the current month is before the birth month
        if (months < 0) {
            years--;
            months += 12;
        }

        // Adjust months if the current day is before the birth day
        if (now.getDate() < dob.getDate()) {
            if (months === 0) {
                years--;
                months = 11;
            } else {
                months--;
            }
        }

        return { years, months };
    };

    if (isStudentDataLoading) {
        return <Typography>Loading...</Typography>;
    }

    if (!studentData) {
        return <Typography>No student data found.</Typography>;
    }

    let pronoun: string = "student";
    let possessivePronoun: string = "student's";
    let subjectPronoun: string = "student";

    if (studentData?.gender.toLowerCase() === "male") {
        pronoun = "him";
        possessivePronoun = "his";
        subjectPronoun = "he";
    }
    else if (studentData?.gender.toLowerCase() === "female") {
        pronoun = "her";
        possessivePronoun = "hers";
        subjectPronoun = "she";
    }


    const overViewText = `${studentData?.name}  participated in a 20 - 25 minute interactive screening exercise
                            designed to understand varied cognitive abilities in children. The
                            exercise is designed as a game and engaging tasks are presented in the
                            form of an animated story. This enables ${studentData?.name} to explore various
                            cognitive skills in a fun, stress - free environment.`;

    const aboutText = `IsoKinder is a comprehensive, gamified app designed to screen for
          children's cognitive skills through an interactive and child-friendly
          screening experience.  By intetrating fun tasks with cognitive
          assessments, IsoKinder provides valuable insights that help caregivers
          support each child's growth effectively.`;

    const overallPerformance =
        studentData?.aggregateData?.[0]?.overallPerformance || 0;

    const overallPerformanceTotal =
        studentData?.aggregateData?.[0]?.overallPerformanceTotal || 0;

    const overallPerformancePercentage = Math.round((overallPerformance / overallPerformanceTotal) * 100)


    let overallScoreText = `This score indicates that additional support could help ${capitalizeName(studentData?.name)} in developing ${possessivePronoun} cognitive skills. Targeted activities and regular intervention can facilitate significant progress.`;
    if (overallPerformancePercentage >= 70) {
        overallScoreText = `This score indicates that ${studentData?.name} has performed exceptionally well in all categories of the screening test. It suggests that ${pronoun} overall performance is strong and reflects a solid understanding of the assessed skills.`;
    }
    else if (overallPerformancePercentage >= 40) {
        overallScoreText = `This score indicates a good level of performance, highlighting ${studentData?.name}'s strengths and abilities. Practicing targeted activities regularly will further refine ${pronoun} skills, allowing ${pronoun} to reach new milestones.`;
    }


    let presentationText = `${capitalizeName(studentData?.name)}  avoided eye contact and communicated hesitantly.`;
    let focussedText = `${capitalizeName(subjectPronoun)} got distracted while playing the game and had difficulty following onscreen instructions.`;
    let digitalSpaceConfidenceText = `${capitalizeName(subjectPronoun)} felt uncertain while navigating the digital space.`;
    let engagedText = `${capitalizeName(subjectPronoun)} did not display enthusiasm and was not engaged with the game.`;

    if (studentData?.aggregateData?.[0]?.observation?.eyeContact) {
        console.log("inside eyecontacts");
        presentationText = `${studentData?.name}  maintained eye contact and communicated confidently.`;
    }

    if (studentData?.aggregateData?.[0]?.observation?.focused) {
        console.log("inside focussed");
        focussedText = `${capitalizeName(subjectPronoun)}  remained focused throughout the game and followed onscreen instructions with ease.`;
    }
    if (studentData?.aggregateData?.[0]?.observation?.digitalSpaceConfidence) {
        console.log("inside confidence");
        digitalSpaceConfidenceText = `${capitalizeName(subjectPronoun)} showed a degree of comfort and enthusiasm in navigating digital tasks.`;
    }
    if (studentData?.aggregateData?.[0]?.observation?.excitedAndHappy) {
        console.log("inside engaged");
        engagedText = `${capitalizeName(subjectPronoun)} demonstrated visible excitement and positive engagement during gameplay.`;
    }



    let remarkText = `${studentData?.name}'s overall performance suggests that ${subjectPronoun} would benefit from additional support and intervention to build ${possessivePronoun} cognitive skills. Consistent guidance, targeted exercises, and a supportive learning environment can help ${pronoun} make meaningful improvements over time.`;
    if (overallPerformancePercentage >= 70) {
        remarkText = `${studentData?.name} has demonstrated strong cognitive abilities and shows strong learning potential. Continued engagement with challenging and stimulating activities can help further enhance ${pronoun} skills and sustain ${pronoun} progress.`;
    }
    else if (overallPerformancePercentage >= 30) {
        remarkText = `${studentData?.name} demonstrates a fair level of cognitive skills, with clear potential for further growth. Continued practice, exposure to challenging tasks, and regular feedback can help ${pronoun} enhance ${possessivePronoun} overall performance.`;
    }

    let disclaimerText = `Disclaimer: This report serves as a basic screening report. For a more detailed analysis, we recommend getting a comprehensive assessment at a child development centre.`;







    const generatePDF = async () => {
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();

        const captureElement = async (element: HTMLElement) => {
            return html2canvas(element, { scale: 2 }).then((canvas) => {
                const imgData = canvas.toDataURL("image/png");
                const imgProps = pdf.getImageProperties(imgData);
                const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
                return { imgData, imgHeight };
            });
        };

        // Capture first element
        if (ref1.current) {
            const { imgData, imgHeight } = await captureElement(ref1.current);
            pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
        }

        // Capture second element on new page
        if (ref2.current) {
            const { imgData, imgHeight } = await captureElement(ref2.current);
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
        }

        // Capture third element on new page
        if (ref3.current) {
            const { imgData, imgHeight } = await captureElement(ref3.current);
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
        }
        const pdfBlob = pdf.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
    }




    return (

        <Box width="100%">
            <Paper elevation={0} ref={ref1} id="studentReportContentP1" sx={{ width: '200mm', padding: 2, }}>  {/*210 is A4 size width*/}
                <Box sx={{ padding: 2 }}>
                    <Box display="flex" flexDirection="row" width="100%">
                        <Box width="75%"><Typography
                            variant="h2"
                            sx={{ color: "black", fontWeight: "bold" }}
                        >
                            {`IsoKinder Performance Report`}
                        </Typography></Box>
                        <Box width="25%" sx={{ display: "flex", justifyContent: "flex-end", }}>
                            <img
                                src="../IsoKinder_logo_without_background.png"
                                alt="IsoKinder Logo"
                                style={{ width: '100px' }}
                            />
                        </Box>

                    </Box>

                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="left"
                        justifyContent="left"
                        rowGap={1}

                    >
                        <Box marginTop={2}>
                            <Typography variant="h4" component="strong">Student Profile </Typography>
                        </Box>
                        <Box>
                            <Typography fontSize="5" > Name: {studentData?.name}</Typography>
                            <Typography fontSize="5"> Class: {studentData?.class} {studentData?.section}</Typography>
                            <Typography fontSize="5">Gender: {studentData?.gender}</Typography>
                            <Typography fontSize="5">
                                Age: {calculateAge(studentData?.dateOfBirth).years} years and{" "}
                                {calculateAge(studentData?.dateOfBirth).months} months
                                <Typography fontSize="5"> School: {studentData?.schoolName}</Typography>
                            </Typography>
                        </Box>
                        <Box marginTop={2}>
                            <Typography variant="h4" component="strong">Overview </Typography>
                        </Box>
                        <Box>
                            <Typography fontSize="5" align="justify">
                                {overViewText}
                            </Typography>
                        </Box>
                        <Box marginTop={2}>
                            <Typography variant="h4" component="strong">Performance Analysis</Typography>
                        </Box>
                        <Box display="flex" flexDirection="row" columnGap="10">
                            <Box>

                                {studentData?.aggregateData && studentData.aggregateData.length > 0 && (
                                    <Box>
                                        <Typography fontSize="h6" sx={{ minWidth: 180 }}>
                                            Overall Score
                                        </Typography>
                                        <PieChart width={100} height={100}>
                                            <Pie

                                                data={[
                                                    { name: "Correct", value: overallPerformancePercentage },
                                                    { name: "Incorrect", value: 100 - overallPerformancePercentage }
                                                ]}
                                                dataKey="value"
                                                innerRadius={30}
                                                outerRadius={40}
                                                paddingAngle={2}
                                                stroke="none"
                                                labelLine={false}
                                                label={({ cx, cy }) => (
                                                    <text
                                                        x={cx}
                                                        y={cy}
                                                        textAnchor="middle"
                                                        dominantBaseline="middle"
                                                        fontSize="1.2rem"
                                                        fontWeight="bold"
                                                        fill={palette.primary[800]}
                                                    >
                                                        {overallPerformancePercentage}%
                                                    </text>
                                                )}
                                            >
                                                <Cell fill={'#FF0000'} />
                                                <Cell fill={'#06ad06'} />
                                            </Pie>
                                        </PieChart>
                                    </Box>
                                )}
                            </Box>
                            <Box marginLeft="30px">

                                <Typography fontSize="5" sx={{ minWidth: 180 }} align="justify">
                                    {overallScoreText}
                                </Typography>
                            </Box>
                        </Box>
                        <Box marginTop={2}>
                            <Typography variant="h4" component="strong">Category-wise Analysis</Typography>
                        </Box>

                        <StudentReport data={studentData?.aggregateData} />
                    </Box>
                </Box>
            </Paper>
            <Paper elevation={0} ref={ref2} id="studentReportContentP2" sx={{ width: '200mm', padding: 2, }}>  {/*A4 size width*/}
                <Box sx={{ padding: 2 }}>
                    <Box display="flex" flexDirection="row" width="100%">
                        <Box width="75%" sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h4" component="strong">Behavioural Observation</Typography>
                        </Box>
                        <Box width="25%" sx={{ display: "flex", justifyContent: "flex-end", }}>

                            <img
                                src="../IsoKinder_logo_without_background.png"
                                alt="IsoKinder Logo"
                                style={{ width: '100px' }}
                            />
                        </Box>
                    </Box>

                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="left"
                        justifyContent="left"
                        rowGap={1}

                    >
                        <Box>
                            <Typography variant="h5" component="strong">Presentation</Typography>
                        </Box>

                        <Box display="flex" flexDirection="row" alignItems="center">
                            {studentData?.aggregateData?.[0]?.observation?.eyeContact ?
                                <Box>
                                    <CheckCircleIcon style={{ color: 'green', fontSize: '2rem' }} />
                                </Box>
                                :
                                <Box>
                                    <CancelIcon style={{ color: 'red', fontSize: '2rem' }} />
                                </Box>
                            }
                            <Typography fontSize="5" sx={{ minWidth: 180 }}>
                                {presentationText}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h5" component="strong">Focus</Typography>
                        </Box>

                        <Box display="flex" flexDirection="row" alignItems="center">
                            {studentData?.aggregateData?.[0]?.observation?.focused ?
                                <Box>
                                    <CheckCircleIcon style={{ color: 'green', fontSize: '2rem' }} />
                                </Box>
                                :
                                <Box>
                                    <CancelIcon style={{ color: 'red', fontSize: '2rem' }} />
                                </Box>
                            }
                            <Typography fontSize="5" sx={{ minWidth: 180 }}>
                                {focussedText}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h5" component="strong">Confidence</Typography>
                        </Box>

                        <Box display="flex" flexDirection="row" alignItems="center">
                            {studentData?.aggregateData?.[0]?.observation?.digitalSpaceConfidence ?
                                <Box>
                                    <CheckCircleIcon style={{ color: 'green', fontSize: '2rem' }} />
                                </Box>
                                :
                                <Box>
                                    <CancelIcon style={{ color: 'red', fontSize: '2rem' }} />
                                </Box>
                            }
                            <Typography fontSize="5" sx={{ minWidth: 180 }}>
                                {digitalSpaceConfidenceText}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h5" component="strong">Engagement</Typography>
                        </Box>

                        <Box display="flex" flexDirection="row" alignItems="center">
                            {studentData?.aggregateData?.[0]?.observation?.excitedAndHappy ?
                                <Box>
                                    <CheckCircleIcon style={{ color: 'green', fontSize: '2rem' }} />
                                </Box>
                                :
                                <Box>
                                    <CancelIcon style={{ color: 'red', fontSize: '2rem' }} />
                                </Box>
                            }
                            <Typography fontSize="5" sx={{ minWidth: 180 }}>
                                {engagedText}
                            </Typography>
                        </Box>



                        <Box marginTop={2}>
                            <Typography variant="h4" component="strong">Remarks</Typography>
                        </Box>
                        <Box>
                            <Typography fontSize="5" sx={{ minWidth: 180 }} align="justify">
                                {remarkText}
                                
                            </Typography>
                        </Box>
                        <Box marginTop={2}>
                            <Typography variant="h4" component="strong">About IsoKinder</Typography>
                        </Box>
                        <Box>
                            <Typography fontSize="5" sx={{ minWidth: 180, mb:2 }} align="justify">
                                {aboutText}
                            </Typography>
                            <Typography fontSize="5" sx={{ minWidth: 180 }} align="justify">
                                {disclaimerText}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
            <Paper elevation={0} ref={ref3} id="studentReportContentP3" sx={{ width: '200mm', padding: 2, }}>  {/*A4 size width*/}
                <Box display="flex" flexDirection="column" sx={{ padding: 2, }}>
                    <Box display="flex" flexDirection="row" width="100%">
                        <Box width="75%" sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h4" component="strong" marginTop="5px">Glossary</Typography>
                        </Box>
                        <Box width="100%" sx={{ display: "flex", justifyContent: "flex-end", }}>
                            <img
                                src="../IsoKinder_logo_without_background.png"
                                alt="IsoKinder Logo"
                                style={{ width: '100px' }}
                            />
                        </Box>
                    </Box>

                    <Typography fontSize="5" sx={{ minWidth: 180 }} align="justify">
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                            <li>
                                <strong>Auditory Discrimination: </strong>It refers to the
                                ability to distinguish between different sounds. It involves
                                auditory recognition, the capacity to differentiate between similar
                                sounds, and the skill to identify and match sounds to their sources.
                            </li>
                            <li>
                                <strong>Baseline: </strong> A reference point or standard
                                used for comparison. In this context, it refers to the average
                                performance percentage for a specific age group in the school. This
                                baseline is utilized to evaluate and compare cognitive abilities
                                against the average performance of children in the same demographic.
                            </li>
                            <li>
                                <strong>Cognitive Abilities: </strong> It refers to the
                                mental skills involved in tasks related to perception, learning,
                                memory, understanding, awareness, reasoning, judgment, intuition,
                                and language. These abilities encompass the processing of
                                information, the ability to reason through problems, remember
                                information over time, and relate objects or concepts.
                            </li>
                            <li>
                                <strong>Identification: </strong>The ability to
                                recognize and label simple, familiar objects, people, or events. It
                                includes basic recognition skills, understanding common vocabulary,
                                and connecting visual stimuli to known concepts.
                            </li>
                            <li>
                                <strong>Intellectual Aptitude: </strong>It refers to the
                                inherent capacity or potential of an individual to acquire and apply
                                knowledge, solve problems, reason logically, and engage in critical
                                thinking.
                            </li>
                            <li>
                                <strong>Intervention: </strong>Specific activities,
                                strategies, or programs designed to improve cognitive abilities or
                                address deficits.
                            </li>
                            <li>
                                <strong>Memory: </strong>Memory encompasses the mental
                                processes of learning, encoding, retaining, and retrieving
                                information or past experiences over time. It involves the capacity
                                to recall information across short or long intervals.
                            </li>
                            <li>
                                <strong>Numerical Skills: </strong>The understanding and
                                ability to work with numbers, including basic arithmetic operations.
                                This covers number recognition, counting, basic addition and
                                subtraction, and an understanding of numerical concepts.
                            </li>
                            <li>
                                <strong>Performance Percentage: </strong>The ratio of
                                correct answers to total questions, expressed as a percentage,
                                indicating the overall performance level.
                            </li>
                            <li>
                                <strong>Reasoning: </strong>The process of using logical
                                processes, inductive or deductive to draw conclusions from facts or
                                premises. It encompasses problem-solving, critical thinking, pattern
                                recognition, and the ability to make decisions based on logical
                                inference.
                            </li>
                            <li >
                                <strong>Visual Discrimination: </strong>It refers to the
                                ability to distinguish between different visual stimuli, such as
                                shapes, colors, and patterns. It involves visual recognition,
                                attention to detail, and the capability to differentiate between
                                similar-looking items or identify shapes, patterns, hidden figures,
                                or other images that differ subtly from each other.
                            </li>
                        </ul>
                    </Typography>
                    <Typography fontSize="5" sx={{ minWidth: 180 }} align="justify" marginTop={2}><strong>Reference:</strong> American Psychological
                        Association. (n.d.). APA Dictionary of Psychology. Retrieved July 5,
                        2024, from https://dictionary.apa.org</Typography>
                </Box>
            </Paper>
            <Button
                sx={{
                    backgroundColor: '#7B2CBF',
                    '&:hover': { backgroundColor: '#6a23b5' },
                    borderRadius: '8px',
                    paddingX: 4,
                    paddingY: 1.2,
                    fontSize: '1rem,',
                    mt: 2,
                }}
                onClick={generatePDF}>
                Generate PDF
            </Button>
        </Box>

    );
};

export default StudentReportPage;
